import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Navigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {NumericFormat} from "react-number-format";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-tooltip/dist/react-tooltip.css";
import Swal from "sweetalert2";

import {
    fetchCompanies,
    fetchOffices,
    fetchCategories,
    fetchEmissionFactors,
    readEmissionRecord,
    updateEmissionRecord,
} from "../../library/api/emission";
import {useDispatch} from "react-redux";
import {setFabMessage} from "../../features/layout/helpFabSlice";

import Attachment from "../../components/Attachment";
import AttachmentDropZone from "../../components/AttachmentDropZone";
import CustomizationInfoBox from "../../components/CustomizationInfoBox";


// ⬇️ same mapping used by CreateEmission
import categoryInfo from "./category_source_links.json";
import {Button, IconButton, Tooltip} from "@mui/material";
import NoAttachmentWarning from "../../components/NoAttachmentWarning";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PageHeader from "../../components/PageHeader";

function AttachmentBody({attachment, t, file, onFileSelect, accept}) {
    const [prefilled, setPrefilled] = useState(false);
    const [dismissedServerDownload, setDismissedServerDownload] = useState(false);

    // Prefill exactly once from server blob and tag it as server-origin
    useEffect(() => {
        if (prefilled) return;
        if (attachment?.blob) {
            const f = new File([attachment.blob], attachment.name, {type: attachment.type});
            // tag the file so we can tell it's the server version
            Object.defineProperty(f, "__fromServer", {value: true, enumerable: false});
            onFileSelect(f);
            setPrefilled(true);
        }
    }, [attachment, prefilled, onFileSelect]);

    // Wrap onFileSelect so clearing or choosing a new local file hides Download
    const handleSelect = useCallback(
        (f) => {
            // If cleared OR user-picked (not server-tagged), permanently hide the server download
            if (!f || !f.__fromServer) setDismissedServerDownload(true);
            onFileSelect(f);
        },
        [onFileSelect]
    );

    // If user still has the prefilled server file selected and hasn't dismissed, keep showing Download
    const showServerDownload =
        !!attachment?.url && !dismissedServerDownload && (!file || file.__fromServer);

    return (
        <AttachmentDropZone
            id="formFile"
            file={file}
            onFileSelect={handleSelect}
            accept={accept}
            downloadUrl={showServerDownload ? attachment.url : undefined}
            downloadName={showServerDownload ? attachment.name : undefined}
            downloadLabel={t("dashboard.btnDownload")}
        />
    );
}

export function AttachmentPrefillAndDropzone({
                                                 attachmentId,
                                                 t,
                                                 file,
                                                 onFileSelect,
                                                 accept = "image/*,.pdf,.doc,.docx",
                                             }) {
    if (!attachmentId) {
        return (
            <AttachmentDropZone
                id="formFile"
                file={file}
                onFileSelect={onFileSelect}
                accept={accept}
            />
        );
    }

    return (
        <Attachment id={attachmentId}>
            {({attachment}) => (
                <AttachmentBody
                    attachment={attachment}
                    t={t}
                    file={file}
                    onFileSelect={onFileSelect}
                    accept={accept}
                />
            )}
        </Attachment>
    );
}

/** Safely get unit array from a factor object (supports multiple shapes) */
function extractUnitsFromFactor(f) {
    return Array.isArray(f?.units) ? f.units : [];
}

function normalizeEmissionFactorsForDisplay(factors) {
    const factorList = Array.isArray(factors) ? factors : [];

    const normalized = factorList.map((factor) => {
        const name = typeof factor?.name === "string" ? factor.name.trim() : "";
        const year = factor?.year;
        const hasYear = year !== undefined && year !== null && year !== "";

        if (!name || !hasYear) return factor;

        return {
            ...factor,
            name: `${name} (${year})`,
        };
    });

    normalized.sort((a, b) =>
        String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
            sensitivity: "base",
            numeric: true,
        })
    );

    return normalized;
}

export default function EmissionUpdate() {
    const {t, i18n} = useTranslation();
    const {id} = useParams();
    const dispatch = useDispatch();

    const [redirectEmission, setRedirectEmission] = useState(false);

    const [listCompany, setListCompany] = useState([]);
    const [listOffice, setListOffice] = useState([]);
    const [listCategory, setListCategory] = useState([]);
    const [listEmissionFactor, setListEmissionFactor] = useState([]); // contains units

    const [selectedCompany, setSelectedCompany] = useState(0);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [selectedOffice, setSelectedOffice] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedFile, setSelectedFile] = useState(null);

    const [selectedCategoryTooltip, setSelectedCategoryTooltip] = useState("");
    const [sourceInfo, setSourceInfo] = useState(null);

    const [isSaving, setIsSaving] = useState(false);

    const [listEmissionRecord, setListEmissionRecord] = useState([
        {
            assets: "",
            factor: 0,
            amount: 0,
            amount_2: 0,
            amount_3: 0,
            unit: 0,
            multiplier: 1,
            attachmentId: undefined,
        },
    ]);

    const breadcrumbAssetRef = useRef(null); // frozen label for breadcrumbs

    const decimalSeparator = i18n.language === "id" ? "," : ".";
    const thousandSeparator = i18n.language === "id" ? "." : ",";

    const loadOffices = useCallback(async (companyId) => {
        const offices = await fetchOffices(companyId);
        setSelectedCompany(companyId);
        setListOffice(offices || []);
    }, []);

    // initial load + read selected record
    useEffect(() => {
        (async () => {
            try {
                const companies = await fetchCompanies();
                setListCompany(companies || []);

                let initialClientId = "";

                if (id) {
                    const rec = await readEmissionRecord(id);
                    if (rec) {
                        initialClientId = String(rec.record_client || "");
                        setSelectedClientId(initialClientId);

                        if (!breadcrumbAssetRef.current) {
                            breadcrumbAssetRef.current = rec.record_assets || "Item";
                        }
                        await loadOffices(rec.record_company);

                        const datePick = new Date(
                            new Date(rec.record_date).getFullYear(),
                            new Date(rec.record_date).getMonth(),
                            7
                        );

                        setSelectedCompany(rec.record_company);
                        setSelectedOffice(rec.record_location);
                        setSelectedCategory(rec.record_category);
                        setSelectedDate(datePick);

                        // set source references like CreateEmission
                        const meta = categoryInfo[String(rec.record_category)];
                        const linked = meta?.source || categoryInfo?.default?.source || null;
                        setSourceInfo(linked || null);

                        // Load factors for this category (with embedded units).
                        // Pass record's own factor so a hidden EF still renders in the dropdown.
                        const factors = await fetchEmissionFactors(
                            rec.record_category,
                            initialClientId,
                            rec.record_factor
                        );
                        setListEmissionFactor(normalizeEmissionFactorsForDisplay(factors));

                        setListEmissionRecord((prev) => {
                            const next = [...prev];
                            next[0] = {
                                ...next[0],
                                assets: rec.record_assets,
                                factor: rec.record_factor,
                                unit: rec.record_unit,
                                amount: rec.record_amount,
                                amount_2: rec.record_amount_2,
                                amount_3: rec.record_amount_3,
                                attachmentId: rec.attachment_id,
                            };

                            const factorObj = (factors || []).find((f) => f.id == rec.record_factor || f.emission_factor_id == rec.record_factor);
                            const unitList = extractUnitsFromFactor(factorObj);
                            const chosenUnit = unitList.find((u) => (u.id ?? u.unit_id) == rec.record_unit);
                            next[0].multiplier = chosenUnit?.multiplier ?? chosenUnit?.unit_multiplier ?? 1;

                            return next;
                        });
                    }
                }

                const categories = await fetchCategories([1, 2], initialClientId);
                setListCategory(categories || []);
            } finally {
            }
        })();
    }, [id, loadOffices]);

    // keep tooltip localized (same logic as CreateEmission)
    useEffect(() => {
        if (!selectedCategory) {
            setSelectedCategoryTooltip("");
            return;
        }
        const cat = (listCategory || []).find((c) => Number(c.id) === Number(selectedCategory));
        if (!cat) {
            setSelectedCategoryTooltip("");
            return;
        }
        const tip = cat.description || cat.descriptionId || "";
        setSelectedCategoryTooltip(tip);
    }, [selectedCategory, listCategory, i18n.language]);

    // company -> office
    const onSelectCompany = async (e) => {
        const companyId = Number(e.target.value) || 0;
        await loadOffices(companyId);
        setSelectedOffice(0);

        const selectedCompanyOption = (listCompany || []).find(
            (item) => Number(item.company_id) === companyId
        );
        const nextClientId = selectedCompanyOption?.company_client
            ? String(selectedCompanyOption.company_client)
            : "";

        setSelectedClientId(nextClientId);

        const categories = await fetchCategories([1, 2], nextClientId);
        setListCategory(categories || []);

        const currentCategoryStillValid = (categories || []).some(
            (item) => Number(item.id) === Number(selectedCategory)
        );
        if (!currentCategoryStillValid) {
            setSelectedCategory(0);
            setListEmissionFactor([]);
            setListEmissionRecord((prev) => {
                const next = [...prev];
                next[0] = {
                    ...next[0],
                    factor: 0,
                    unit: 0,
                    multiplier: 1,
                };
                return next;
            });
        }
    };

    const onSelectOffice = (e) => setSelectedOffice(e.target.value);

    const onSelectDate = (date) => {
        const d = new Date(date);
        setSelectedDate(new Date(d.getFullYear(), d.getMonth(), 7));
    };

    // category -> refetch factors + set references (like CreateEmission)
    const onSelectCategory = async (e) => {
        const categoryValue = Number(e.target.value) || 0;
        setSelectedCategory(categoryValue);

        const meta = categoryInfo[String(categoryValue)];
        const linked = meta?.source || categoryInfo?.default?.source || null;
        setSourceInfo(categoryValue > 0 ? linked : null);

        const nextAd = meta?.ad ?? null;
        dispatch(
            setFabMessage({
                title: "More with TruCarbon",
                message: nextAd,
                whatsappText: "Hello TruCarbon, I want to learn more about TruCarbon's services.",
            })
        );

        const factors = await fetchEmissionFactors(categoryValue, selectedClientId);
        setListEmissionFactor(normalizeEmissionFactorsForDisplay(factors));

        setListEmissionRecord([
            {
                assets: "",
                factor: 0,
                amount: "",
                amount_2: "",
                amount_3: "",
                unit: 0,
                multiplier: 1,
                attachmentId: listEmissionRecord?.[0]?.attachmentId,
            },
        ]);
    };

    const onInputAsset = (e, index) => {
        const v = e.target.value;
        setListEmissionRecord((prev) => {
            const next = [...prev];
            next[index] = {...next[index], assets: v};
            return next;
        });
    };

    const handleFactorClick = () => {
        dispatch(
            setFabMessage({
                title: "setting.emission.custom.title",
                message: "setting.emission.custom.hook",
                whatsappText: "Hello TruCarbon, I need customized emission factors.",
            })
        );
    };

    const onSelectEmission = (e, index) => {
        const v = e.target.value;
        setListEmissionRecord((prev) => {
            const next = [...prev];
            next[index] = {...next[index], factor: v, unit: 0, multiplier: 1};
            return next;
        });
    };

    const onSelectUnit = (e, index) => {
        const v = e.target.value;
        const factorId = listEmissionRecord?.[index]?.factor;
        const factorObj = (listEmissionFactor || []).find((f) => f.id == factorId || f.emission_factor_id == factorId);
        const unitList = extractUnitsFromFactor(factorObj);
        const chosen = unitList.find((u) => (u.id ?? u.unit_id) == v);
        const multiplier = chosen?.multiplier ?? chosen?.unit_multiplier ?? 1;

        setListEmissionRecord((prev) => {
            const next = [...prev];
            next[index] = {...next[index], unit: v, multiplier};
            return next;
        });
    };

    const onAmountChange = (values, sourceInfo, index) => {
        if (sourceInfo?.event?.target?.name && values) {
            setListEmissionRecord((prev) => {
                const next = [...prev];
                next[index] = {...next[index], amount: values.value};
                return next;
            });
        }
    };

    const onCODChange = (values, sourceInfo, index) => {
        if (sourceInfo?.event?.target?.name && values) {
            const key = sourceInfo.event.target.name; // amount_2 | amount_3
            setListEmissionRecord((prev) => {
                const next = [...prev];
                next[index] = {...next[index], [key]: values.value};
                return next;
            });
        }
    };

    const onSave = async () => {
        setIsSaving(true);
        const categoryMeta = (listCategory || []).find((c) => c.id == selectedCategory || c.category_id == selectedCategory);
        const scope = categoryMeta?.scope ?? categoryMeta?.category_scope;

        const element = listEmissionRecord[0] || {};
        const factorObj = (listEmissionFactor || []).find((f) => f.id == element.factor || f.emission_factor_id == element.factor);
        const unitList = extractUnitsFromFactor(factorObj);
        const chosenUnit = unitList.find((u) => (u.id ?? u.unit_id) == element.unit);

        const ef = parseFloat(factorObj?.emission_factor ?? factorObj?.value ?? 1);
        const unitMultiplier = element.multiplier ?? chosenUnit?.multiplier ?? chosenUnit?.unit_multiplier ?? 1;
        const total = parseFloat(element.amount || 0) * unitMultiplier * (factorObj ? ef : 1);

        const fd = new FormData();
        fd.append("id", id);
        fd.append("scope", scope);
        fd.append("category", selectedCategory);
        fd.append("factor", element.factor);
        fd.append("unit", element.unit);
        fd.append("company", selectedCompany);
        fd.append("location", selectedOffice);
        if (selectedClientId) {
            fd.append("clientId", selectedClientId);
            fd.append("client_id", selectedClientId);
        }
        fd.append("assets", element.assets);
        fd.append("date", selectedDate.toISOString());
        fd.append("amount", element.amount);
        fd.append("total", factorObj ? total : parseFloat(element.amount || 0) * unitMultiplier);
        if (selectedFile) fd.append("attachment", selectedFile);
        if (selectedCategory == 26) {
            fd.append("amount_2", element.amount_2);
            fd.append("amount_3", element.amount_3);
        }

        const ok = await updateEmissionRecord(fd);
        if (!ok) {
            Swal.fire({
                icon: "warning",
                title: "",
                html: "<p>Please fill all information needs</p>",
                showCancelButton: false,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Ok",
            });
            setIsSaving(false);
            return;
        }

        await Swal.fire({
            icon: "success",
            title: "",
            html: "<p>Successfully record emissions</p>",
            showCancelButton: false,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
        });

        setRedirectEmission(true);
        setIsSaving(false);
    };

    const labelForFactor = (() => {
        const c = Number(selectedCategory);
        if (c === 25) return t("emission.form.mineDepth");
        if ([22, 21, 20].includes(c)) return t("emission.form.label21");
        if (c === 18) return t("emission.form.label22");
        if (c === 7) return t("emission.form.label8");
        if (c === 4 || c === 26) return t("emission.form.label19");
        if (c === 3) return t("emission.form.label9");
        if (c === 5) return t("emission.form.label26");
        if (c === 6) return t("emission.form.label25");
        if (c === 12) return t("emission.form.label27");
        if (c === 13) return t("emission.form.label28");
        return t("emission.form.label10");
    })();

    const amountLabel = (() => {
        const c = Number(selectedCategory);
        if (c === 4) return t("emission.form.label20");
        if (c === 5) return t("emission.form.label12");
        return t("emission.form.label13");
    })();

    if (redirectEmission) return <Navigate to="/emission"/>;

    // Resolve selected unit for the amount suffix
    const selectedFactorId = listEmissionRecord?.[0]?.factor;
    const factorObjForAmount = (listEmissionFactor || []).find(
        (f) => f.id == selectedFactorId || f.emission_factor_id == selectedFactorId
    );
    const currentUnits = extractUnitsFromFactor(factorObjForAmount);
    const selectedUnitObj = currentUnits.find(
        (u) => (u.id ?? u.unit_id) == listEmissionRecord?.[0]?.unit
    );
// Normalize a readable label
    const selectedUnitLabel = selectedUnitObj?.name

    return (
        <div className="page-content">
            <div className="container-fluid">
                <PageHeader
                    title={t("general.update")}
                    labels={{
                        emission: t("menu.label21"),
                        update: {text: "Scope 1 & 2", link: false},   // not clickable
                    }}
                    paramLabels={{
                        id: breadcrumbAssetRef.current || "",
                    }}
                    hideUnlabeledParams={true}
                    linkLast={false}
                    home={true}
                />

                <div className="card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="company"
                                           className="form-label">{t("emission.form.label1")}</label>
                                    <select
                                        onChange={onSelectCompany}
                                        value={selectedCompany}
                                        id="company"
                                        name="company"
                                        className="form-select mb-3"
                                    >
                                        {!selectedCompany &&
                                            <option value="0">{t("emission.form.placeholder1")}</option>}
                                        {listCompany.map((item) => (
                                            <option key={item.company_id} value={item.company_id}>
                                                {item.company_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="office"
                                           className="form-label">{t("emission.table.label2")}</label>
                                    <select
                                        onChange={onSelectOffice}
                                        value={selectedOffice}
                                        id="office"
                                        name="office"
                                        className="form-select mb-3"
                                        disabled={!selectedCompany}
                                    >
                                        {!selectedOffice &&
                                            <option value="0">{t("emission.form.placeholder2")}</option>}
                                        {listOffice.map((item) => (
                                            <option key={item.office_id} value={item.office_id}>
                                                {item.office_name}{item.office_address ? ` (${item.office_address})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="date"
                                           className="form-label">{t("emission.form.label4")}</label>
                                    <div>
                                        <DatePicker
                                            id="date"
                                            selected={selectedDate}
                                            onChange={onSelectDate}
                                            dateFormat="MMMM yyyy"
                                            className="form-select mb-3"
                                            showMonthYearPicker
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="category"
                                           className="form-label">{t("emission.form.label2")}</label>

                                    <div className="input-group">
                                        <select
                                            onChange={onSelectCategory}
                                            value={selectedCategory}
                                            id="category"
                                            name="category"
                                            className="form-select"
                                        >
                                            <option value="0">Select Source</option>
                                            {listCategory.map((item) => (
                                                <option key={item.id} value={item.id}>
                                                    {item.name || item.nameId || ""}
                                                </option>
                                            ))}                                                </select>
                                        <span
                                            className="input-group-text"
                                            id="basic-addon2"
                                            title={selectedCategoryTooltip}
                                        >
                                          <i className="ri-information-line"></i>
                                        </span>
                                    </div>

                                    {sourceInfo && (
                                        <p className="mt-2">
                                            &nbsp;{t("emission.form.label3")} :
                                            <a target="_blank" rel="noreferrer" href={sourceInfo.href}>
                                                &nbsp;{sourceInfo.text}
                                            </a>
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="factor" className="form-label">{labelForFactor}</label>
                                    <select
                                        id="factor"
                                        onClick={handleFactorClick}
                                        onChange={(e) => onSelectEmission(e, 0)}
                                        value={listEmissionRecord[0].factor}
                                        className="form-select mb-0"
                                        disabled={!selectedCategory}
                                    >
                                        <option value="0">
                                            {t("emission.form.select")} {labelForFactor}
                                        </option>
                                        {listEmissionFactor.map((f) => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))} '
                                    </select>
                                    {!!selectedCategory && listEmissionFactor.length === 0 && (
                                        <p className="mt-2">
                                            No emission factors available for this category
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label htmlFor="unit"
                                           className="form-label">{t("emission.form.label11")}</label>
                                    <select
                                        id="unit"
                                        onChange={(e) => onSelectUnit(e, 0)}
                                        value={listEmissionRecord[0].unit}
                                        className="form-select mb-0"
                                        disabled={listEmissionRecord[0].factor == 0}
                                    >
                                        <option value="0">{t("emission.form.placeholder4")}</option>
                                        {currentUnits?.length > 0 ? (
                                            currentUnits.map((u) => (
                                                <option key={u.id} value={u.id}>{u.name}</option>
                                            ))
                                        ) : (
                                            <option value="0" disabled>
                                                {"No units available for this factor"}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {selectedCategory == 26 && (
                                <>
                                    <div className="col-md-6">
                                        <div className="mb-3" id="tour__inletCOD">
                                            <label htmlFor="amount_2"
                                                   className="form-label">{t("emission.form.inletCOD")}</label>
                                            <NumericFormat
                                                name="amount_2"
                                                className="form-control"
                                                value={listEmissionRecord[0].amount_2}
                                                onValueChange={(values, si) => onCODChange(values, si, 0)}
                                                decimalSeparator={decimalSeparator}
                                                thousandSeparator={thousandSeparator}
                                                placeholder={t("emission.form.enter")}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-3" id="tour__outletCOD">
                                            <label htmlFor="amount_3"
                                                   className="form-label">{t("emission.form.outletCOD")}</label>
                                            <NumericFormat
                                                name="amount_3"
                                                className="form-control"
                                                value={listEmissionRecord[0].amount_3}
                                                onValueChange={(values, si) => onCODChange(values, si, 0)}
                                                decimalSeparator={decimalSeparator}
                                                thousandSeparator={thousandSeparator}
                                                placeholder={t("emission.form.enter")}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="asset"
                                           className="form-label">{t("emission.form.label7")}</label>
                                    <input
                                        id="asset"
                                        onChange={(e) => onInputAsset(e, 0)}
                                        value={listEmissionRecord[0].assets}
                                        className="form-control"
                                        placeholder={t("emission.form.enter")}
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="amount" className="form-label">{amountLabel}</label>

                                    <div className="input-group">
                                        <NumericFormat
                                            id="amount"
                                            name="amount"
                                            className="form-control"
                                            value={listEmissionRecord[0].amount}
                                            onValueChange={(values, sourceInfo) => onAmountChange(values, sourceInfo, 0)}
                                            decimalSeparator={decimalSeparator}
                                            thousandSeparator={thousandSeparator}
                                            placeholder={t("emission.form.enter")}
                                            allowNegative={false}
                                            isAllowed={(vals) => (vals.floatValue ?? 0) >= 0}
                                            inputMode="decimal"
                                            disabled={!listEmissionRecord[0].unit}
                                        />
                                        <span
                                            className="input-group-text"
                                            title={
                                                selectedUnitLabel
                                                    ? selectedUnitLabel
                                                    : (t("emission.form.selectUnitFirst") || "Select unit first")
                                            }
                                            aria-live="polite">
                                                  {selectedUnitLabel || "—"}
                                                </span>
                                    </div>
                                </div>
                            </div>

                            {/* Attachment */}
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="formFile"
                                           className="form-label d-inline-flex align-items-center">
                                        {t("emission.form.label15")}
                                        <Tooltip
                                            title={
                                                <NoAttachmentWarning/>
                                            }
                                            arrow
                                            placement="top"
                                            enterDelay={120}
                                            leaveDelay={60}
                                            disableInteractive={false}
                                            PopperProps={{
                                                modifiers: [{name: "offset", options: {offset: [0, 8]}}],
                                            }}
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        p: 0,
                                                        bgcolor: "background.paper",
                                                        boxShadow: 3,
                                                        borderRadius: 1,
                                                        maxWidth: 500,
                                                    },
                                                },
                                                arrow: {sx: {color: "background.paper"}},
                                            }}
                                        >
                                                    <span className="d-inline-flex align-items-center" tabIndex={0}>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={t("general.info") || "Info"}
                                                          sx={{ml: 0.5, opacity: 0.8, "&:hover": {opacity: 1}}}
                                                      >
                                                        <InfoOutlinedIcon fontSize="small"/>
                                                      </IconButton>
                                                    </span>
                                        </Tooltip>
                                    </label>                                            <p
                                    className="text-muted">
                                    - {t("emission.form.label24")}
                                    <br/>- {t("emission.form.label16")}
                                </p>
                                    <AttachmentPrefillAndDropzone
                                        attachmentId={listEmissionRecord[0].attachmentId}
                                        t={t}
                                        file={selectedFile}
                                        onFileSelect={setSelectedFile}
                                    />
                                </div>
                            </div>

                            <div className="col-lg-12">
                                <div className="text-start">
                                    <button onClick={onSave} type="submit" className="btn btn-primary"
                                            disabled={isSaving}>
                                        {isSaving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"
                                                      role="status" aria-hidden="true"/>
                                                {t("general.btnSaving")}
                                            </>
                                        ) : (
                                            t("general.btnSave")
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
